import * as React from 'react';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/ui/icon';

/**
 * The controls the menu wizard's frames need and nothing else does: a described
 * radio group, a row checkbox, a filter chip and a toned callout.
 *
 * The pill switch that used to live here is now `components/ui/switch.tsx` —
 * three groups had grown one. These four have a single caller each, so they stay
 * beside it rather than joining a shared directory on the strength of one use.
 *
 * Direction: no physical sides anywhere, and no `letter-spacing` on anything —
 * Arabic letterforms join, and spacing them apart breaks the word.
 */

export interface RadioOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

/**
 * A radio group as a `fieldset`/`legend` rather than a `<Field>`.
 *
 * `Field` renders one `<label htmlFor>` and threads one id through context —
 * exactly right for one control, and wrong for a set of them: the label would
 * name whichever radio happened to take the id, and the other would be
 * anonymous. `legend` is the element that names a group, and grouping the
 * radios in a fieldset is also what gives a screen reader "1 of 2".
 */
export function RadioGroup<T extends string>({
  legend,
  hint,
  name,
  value,
  options,
  onChange,
}: {
  legend: string;
  hint?: string;
  name: string;
  value: T;
  options: RadioOption<T>[];
  onChange: (next: T) => void;
}) {
  const hintId = React.useId();

  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1.5 block text-sm font-medium">{legend}</legend>
      <div
        className="grid gap-3 sm:grid-cols-2"
        aria-describedby={hint ? hintId : undefined}
      >
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex cursor-pointer items-start gap-2 rounded-lg border p-3 transition-colors',
              option.value === value
                ? 'border-accent-line bg-accent-soft'
                : 'border-line hover:bg-hover',
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              onChange={() => onChange(option.value)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[var(--accent-solid)]"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{option.label}</span>
              {option.hint && (
                <span className="block text-xs text-ink-muted">{option.hint}</span>
              )}
            </span>
          </label>
        ))}
      </div>
      {hint && (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      )}
    </fieldset>
  );
}

/**
 * A whole row that toggles.
 *
 * The `<label>` wraps the row, so the price at the far end is as clickable as
 * the box — on a phone the 16px box alone is the difference between a picker
 * that works and one that misses.
 */
export function CheckRow({
  checked,
  onChange,
  disabled,
  children,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors',
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-hover',
        className,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 shrink-0 accent-[var(--accent-solid)]"
      />
      {children}
    </label>
  );
}

/** A filter chip. `aria-pressed` rather than a tab — these narrow a list, they
 *  do not switch a panel, and calling them tabs would promise arrow keys. */
export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full px-3 py-1 text-xs transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-accent-solid text-accent-on'
          : 'border border-line text-ink-muted hover:bg-hover',
      )}
    >
      {children}
    </button>
  );
}

const CALLOUT_TONES = {
  success: {
    className: 'border-green-600/40 bg-green-500/10 text-green-700 dark:text-green-400',
    glyph: CircleCheck,
  },
  warning: {
    className: 'border-amber-600/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    glyph: TriangleAlert,
  },
} as const;

/**
 * The review step's banner.
 *
 * `role="status"` because its whole job is to change as the draft does — an
 * operator who unticks Active on step 1 and comes back must be told the promise
 * changed, not left reading a stale green tick.
 */
export function Callout({
  tone,
  title,
  body,
}: {
  tone: keyof typeof CALLOUT_TONES;
  title: string;
  /** A node, not a string: the edit path's warning is a list of consequences,
   *  and flattening several of them into one sentence is how the third one
   *  stops being read. */
  body: React.ReactNode;
}) {
  const { className, glyph } = CALLOUT_TONES[tone];
  return (
    <div role="status" className={cn('flex items-start gap-3 rounded-xl border p-4', className)}>
      <Icon as={glyph} size="lg" className="mt-0.5 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <div className="text-sm text-ink-muted">{body}</div>
      </div>
    </div>
  );
}
